// @flow
import {compact} from 'lodash';
import * as React from 'react';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import type {
  ConnectAcceptableVerificationDocumentsContext,
  ConnectVerificationDocumentCategories,
} from 'docs/types';
import {Callout} from '../../core/Callout/Callout';

import type {CountryDisplayName} from '../../core/CountryName/CountryName';
import {TabGroup} from '../../core/Tabs/TabGroup';
import {ContentGroupItem} from '../../../util/ContentGroupItem';
import {Tab} from '../../core/Tabs/Tab';
import {List} from '../../core/List/List';
import {ListItem} from '../../core/List/ListItem';

type ExpirationPolicy =
  | 'document_with_expiry_date'
  | 'government_issued_document_6_months'
  | 'government_issued_document_12_months'
  | 'bill_or_statement_6_months'
  | 'bill_or_statement_12_months'
  | 'contract_of_sale_2_years';

const messages = defineMessages({
  passportCallout: {
    id: 'docsShell.verificationDocuments.passportCallout',
    description:
      'A callout above the list of accepted identity document types, emphasizing that a passport is always preferred',
    defaultMessage:
      'Acceptable identity documents vary by country, however, a passport scan for identity verification is always acceptable and is preferred.',
  },
  emiratesIDCallout: {
    id: 'docsShell.verificationDocuments.emiratesIDCallout',
    description:
      'A callout above the list of accepted identity document types, emphasizing that an Emirates ID (EID) is preferred but in certain circumstances others can be provided.',
    defaultMessage:
      'For a resident UAE national both Emirates ID and passport are required. For a resident who is not a UAE national, an additional visa page is required. For an individual who is neither a UAE national nor resident, a passport is required. Power of Attorney is required for LLCs when the representative is not present on the Memorandum of Association.',
  },
  documentLocalizedName: {
    id: 'docsShell.verificationDocuments.documentLocalizedName',
    description:
      'How to present a document type when both a localized and generic name are available. For example, `localized_name` would be "Australian Defence Force (ADF) identification card" and `name` would be "Military ID".',
    defaultMessage: '{localized_name} ({name})',
  },
  documentTwoSided: {
    id: 'docsShell.verificationDocuments.documentTwoSided.1',
    description: 'A document for which both sides must be scanned.',
    defaultMessage: 'scans of front and back are required',
  },
  identificationTitle: {
    id: 'docsShell.verificationDocuments.identificationTitle',
    description: 'Heading above a list of identity verification documents',
    defaultMessage: 'Acceptable forms of identification:',
  },
  addressTitle: {
    id: 'docsShell.verificationDocuments.addressTitle',
    description: 'Heading above a list of address verification documents',
    defaultMessage: 'Acceptable forms of address verification:',
  },
  legalTitle: {
    id: 'docsShell.verificationDocuments.legalTitle',
    description:
      'Heading above a list of legal entity or company verification documents',
    defaultMessage: 'Acceptable forms of legal entity verification:',
  },
  identificationTab: {
    id: 'docsShell.verificationDocuments.identificationTab',
    description: 'Tab title of a list of identity verification documents',
    defaultMessage: 'Identity Documents',
  },
  legalTabAEDisclaimer: {
    id: 'docsShell.verificationDocuments.identificationTabAEDisclaimer',
    description: 'A disclaimer that should appear on the UAE documents tab',
    defaultMessage:
      'A trade license is a required document for all entity types. One or more Memoranda of Association and a Power of Attorney will also be required depending on the entity type.',
  },
  addressTab: {
    id: 'docsShell.verificationDocuments.addressTab',
    description: 'Tab title of a list of address verification documents',
    defaultMessage: 'Address Documents',
  },
  legalTab: {
    id: 'docsShell.verificationDocuments.legalTab',
    description:
      'Tab title of a list of legal entity or company verification documents',
    defaultMessage: 'Company/Entity Documents',
  },
  expirationPolicy6Months: {
    id: 'docsShell.verificationDocuments.expirationPolicy.6months',
    description:
      'Additional description for documents that must be dated within 6 months',
    defaultMessage: 'dated within 6 months',
  },
  expirationPolicy12Months: {
    id: 'docsShell.verificationDocuments.expirationPolicy.12months',
    description:
      'Additional description for documents that must be dated within 12 months',
    defaultMessage: 'dated within 12 months',
  },
  country: {
    id: 'docsShell.verificationDocuments.country',
    description:
      'Appears before a country dropdown, like this: `Country: <country dropdown>`',
    defaultMessage: 'Country',
  },
});

type VerificationDocumentsProps = {|
  acceptableDocs: $PropertyType<
    ConnectAcceptableVerificationDocumentsContext,
    'acceptable_docs',
  >,
  countries: Array<CountryDisplayName>,
|};

export const AcceptableVerificationDocuments = ({
  acceptableDocs,
  countries,
}: VerificationDocumentsProps) => {
  const intl = useIntl();
  const countryTabs = acceptableDocs.map((docs) => {
    const countryCode = docs.country;
    const countryName =
      countries.find((c) => c.code === docs.country)?.name ?? docs.country;
    return new ContentGroupItem(
      `tab${docs.country}`,
      countryName,
      (<CountryTabContents country={countryCode} docs={docs} />),
    );
  });
  return (
    <TabGroup
      label={intl.formatMessage(messages.country)}
      selector="drop-down"
      items={countryTabs}
    />
  );
};

const CountryTabContents = ({
  country,
  docs,
}: {|
  country: string,
  docs: ConnectVerificationDocumentCategories,
|}): React.Node => {
  const intl = useIntl();
  const {identity, address, entity} = docs;
  // right now we are only showing additional information for the UAE. cleaner logic should be used for regionalizing
  // if more countries want to add additional information in these document tabs.
  const showAECallouts = country === 'AE';

  const getExpirationMessage = (policy: ExpirationPolicy) => {
    if (policy === 'document_with_expiry_date') {
      return null;
    } else if (
      policy === 'government_issued_document_6_months' ||
      policy === 'government_issued_document_12_months'
    ) {
      return null;
    } else if (policy === 'bill_or_statement_6_months') {
      return messages.expirationPolicy6Months;
    } else if (policy === 'bill_or_statement_12_months') {
      return messages.expirationPolicy12Months;
    } else if (policy === 'contract_of_sale_2_years') {
      return null;
    } else {
      return null; // Soft asserts not supported in docs yet.
    }
  };

  const renderIdentityTabCallout = () => {
    let message;
    if (country === 'TH') {
      return null;
    } else if (country === 'AE') {
      message = intl.formatMessage(messages.emiratesIDCallout);
    } else {
      message = intl.formatMessage(messages.passportCallout);
    }
    return <Callout type="note" title={message} />;
  };

  const renderDocLi = (doc) => {
    let docName = doc.name;
    if (doc.localized_name) {
      docName = !doc.localized_name
        .toLowerCase()
        .includes(doc.name.toLowerCase())
        ? intl.formatMessage(messages.documentLocalizedName, {
            name: doc.name,
            localized_name: doc.localized_name,
          })
        : doc.localized_name;
    }

    const extraMessages = [];

    if (doc.expiration_policy) {
      const message = getExpirationMessage(doc.expiration_policy);

      if (message) {
        extraMessages.push(intl.formatMessage(message));
      }
    }

    if (doc.double_sided) {
      extraMessages.push(intl.formatMessage(messages.documentTwoSided));
    }

    return (
      <ListItem key={doc.name}>
        {docName}
        {extraMessages.length > 0 && ' - '}
        {extraMessages.flatMap((item, idx) => [idx ? ', ' : '', item])}
      </ListItem>
    );
  };

  const identityTab = identity != null && identity.length > 0 && (
    <Tab>
      {renderIdentityTabCallout()}
      <p>
        <FormattedMessage {...messages.identificationTitle} />
      </p>
      <List>{identity.map((doc) => renderDocLi(doc))}</List>
    </Tab>
  );
  const addressTab = address != null && address.length > 0 && (
    <Tab>
      <p>
        <FormattedMessage {...messages.addressTitle} />
      </p>
      <List>{address.map((doc) => renderDocLi(doc))}</List>
    </Tab>
  );
  const entityTab = entity != null && entity.length > 0 && (
    <Tab>
      {showAECallouts && (
        <Callout
          type="note"
          title={intl.formatMessage(messages.legalTabAEDisclaimer)}
        />
      )}
      <p>
        <FormattedMessage {...messages.legalTitle} />
      </p>
      <List>{entity.map((doc) => renderDocLi(doc))}</List>
    </Tab>
  );
  const items = compact([
    identityTab
      ? new ContentGroupItem(
          'identity',
          intl.formatMessage(messages.identificationTab),
          identityTab,
        )
      : null,
    addressTab
      ? new ContentGroupItem(
          'address',
          intl.formatMessage(messages.addressTab),
          addressTab,
        )
      : null,
    entityTab
      ? new ContentGroupItem(
          'entity',
          intl.formatMessage(messages.legalTab),
          entityTab,
        )
      : null,
  ]);
  return (
    <Tab>
      {items.length > 0 ? <TabGroup selector="tabs" items={items} /> : null}
    </Tab>
  );
};
